import { useContext, useState, useEffect } from 'react';
import { useRouter } from '@framework/router';
import Flex from '@creditflowresearch/uilib/Flex';
import { useBreadcrumbs, useMounted } from '@/creditflowresearch/ui/common/hooks';
import { initProfilePage } from '@/creditflowresearch/application/actions/profile/actions';
import { useSetMetaTags } from '@/creditflowresearch/application/actions/profile/hooks/useSetMetaTags';
import DealTileWithFallback from '@/creditflowresearch/ui/components/deals/DealTiles/DealTileWithFallback';
import IssueTile from '@/creditflowresearch/ui/components/deals/DealTiles/IssueTile';
import TopCompanies from '@/creditflowresearch/ui/components/TopCompanies';
import AbsMarketChart from '@/creditflowresearch/ui/common/components/MarketChart';
import { ProfileTypes } from '@/creditflowresearch/application/actions/profile/constants';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import ResearchTile from '@/creditflowresearch/ui/components/research/components/ResearchTile';
import MetaSchema from '@/creditflowresearch/ui/common/components/MetaSchema';
import { BondsTileWithFallback } from '@/creditflowresearch/ui/components/bonds/BondsTile';
import { TraceTileWithFallback } from '@/creditflowresearch/ui/components/trace/TraceTile';
import { ArticlesTilesWithFallback } from '@/creditflowresearch/ui/components/creditFlow/articles/ArticlesTiles';
import StatsTileWithFallback from '@/creditflowresearch/ui/components/creditFlow/Stats/StatsTile/StatsTileWithFallback';
import getProfileOverviewSchema from './getProfileMetaSchema';
import getProfileBreadcrumbs from './getProfileBreadcrumbs';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import useHeaderMenuData from '@/creditflowresearch/application/HeaderMenu/useHeaderMenuData';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { RoadshowsTileWithFallback } from '@/creditflowresearch/ui/components/Roadshows/Tile';
import { useCorporateFilters, useAbsRegionsFilters } from '@/creditflowresearch/application/PageFilters/hooks';
import { useFeatureToggleContext } from '@framework/ui/Organisms/FeatureToggle/Context';
import { HIDE_TOP_COMPANIES } from '@creditflowresearch/config/featureToggle';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';

import styles from './profile.module.scss';

interface IProps {
  profileType?: ProfileTypes,
  isConsumerOverview?: boolean,
}

const Profile = ({ profileType, isConsumerOverview = false }: IProps) => {
  const legacy = useContext(DictionariesContext);
  const { isHeaderInitialized, mapping } = useHeaderMenuData();
  const { products, regions, mappedIndustries, mappedSectors } = useHeaderMenuContext();
  const industriesList = legacy?.industriesList ?? mappedIndustries;
  const sectorsList = legacy?.sectorsList ?? mappedSectors;
  const panelBackground = useProductGradient();
  const [pageHeader, setPageHeader] = useState<string>('');
  const router = useRouter();
  const { query, asPath } = router;
  const { industry, sector, routeType }: any = query;
  const setMetaTags = useSetMetaTags();
  const {
    pageFilters,
    checkAccessByUserRegions,
    checkAccessByUserProducts,
  } = usePageFiltersContext();
  const isCorporate = Boolean(routeType && routeType.includes('corporate'));
  const isCorporatePage = isCorporate || Boolean(pageFilters.industry);
  const isMounted = useMounted();
  const { getFeatureByName } = useFeatureToggleContext();
  const { isEnabled: showTopCompanies } = getFeatureByName(HIDE_TOP_COMPANIES);

  const { checkAccessByUserPlan } = useUserPlanContext();
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);
  // TODO: remove when the European market opening period ends
  const userAccessChecks = [
    isOpenAccessPage || checkAccessByUserRegions(),
    isOpenAccessPage || checkAccessByUserProducts(),
  ];

  // TODO substitute to useCorporateMarketFilters
  const corporateFilters = useCorporateFilters({
    isProductsVisible: isCorporatePage,
  });
  const absRegionsFilters = useAbsRegionsFilters();

  const getPageHeaderFilters = () => {
    if (profileType === ProfileTypes.OVERVIEW) {
      return isCorporate ? corporateFilters : absRegionsFilters;
    }

    return corporateFilters;
  };

  const initPageTitle = (pageHeader) => {
    return pageHeader + (isConsumerOverview ? ' (ex CLO/CMBS/RMBS)' : '');
  };

  const pageTitle = initPageTitle(pageHeader);

  const init = (skipNextUpdate: boolean = false) => {
    const headerData = {
      products,
      regions,
      mapping,
      industriesList,
      sectorsList,
    };
    initProfilePage({
      skipNextUpdate,
      profileType,
      isConsumerOverview,
      query,
      pathname: asPath,
      headerData,
      router,
    });
  };

  const updatePageHeaderAndTileText = (pageHeader: string = 'Market Overview') => {
    // we're on industry/subindustry profile page
    if (industry && profileType === ProfileTypes.INDUSTRY) {
      const { pageTitleName = '' } = industriesList[industry] || {};
      pageHeader = pageTitleName;
    }

    // we're on sector/subsector profile page
    if (sector && profileType === ProfileTypes.SECTOR) {
      const { pageTitleName = '' } = sectorsList[sector] || {};
      pageHeader = pageTitleName;
    }

    setPageHeader(pageHeader);
  };

  const getBreadcrumbs = () => {
    if (profileType === ProfileTypes.OVERVIEW) {
      return getProfileBreadcrumbs({
        profileType,
      }, query);
    }

    // we're on industry/subindustry profile page
    if (profileType === ProfileTypes.INDUSTRY && industriesList[industry]) {
      const { industryName, subindustryName } = industriesList[industry];
      return getProfileBreadcrumbs({
        profileType,
        itemName: industryName,
        subitemName: subindustryName,
      }, query);
    }

    // we're on sector/subsector profile page
    if (profileType === ProfileTypes.SECTOR && sectorsList[sector]) {
      const { sectorName, subsectorName } = sectorsList[sector];
      return getProfileBreadcrumbs({
        profileType,
        itemName: sectorName,
        subitemName: subsectorName,
      }, query);
    }

    return null;
  };

  const getSchema = () => {
    if (profileType === ProfileTypes.OVERVIEW) {
      return getProfileOverviewSchema(asPath);
    }
    return null;
  };

  useBreadcrumbs(
    () => getBreadcrumbs(),
    [isHeaderInitialized, pageFilters, industry, sector, query, profileType, industriesList, sectorsList],
  );

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    updatePageHeaderAndTileText();
    if (isHeaderInitialized) {
      init();
    }
  }, [isHeaderInitialized, query, isMounted]);

  useEffect(() => {
    updatePageHeaderAndTileText();
  }, [isHeaderInitialized, pageFilters, industry, sector]);

  useEffect(() => {
    if (isHeaderInitialized) {
      setMetaTags({
        isConsumerOverview,
        profileType,
        industry: pageFilters.industry,
        sector: pageFilters.sector,
        pathname: asPath,
      });
    }
  }, [isHeaderInitialized, pageFilters, profileType, isConsumerOverview, asPath, setMetaTags]);

  return (
    <>
      <MetaSchema schema={ getSchema() } />
      <div className={ styles.profileLayout }>
        <ProductPageHeader
          title={ pageTitle }
          items={ getPageHeaderFilters() }
          // TODO: remove when the European market opening period ends
          accessConditions={ [{ checks: [isOpenAccessPage || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
          // TODO: remove when the European market opening period ends
          blockingAccessConditions={ [{ checks: [isOpenAccessPage || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
          panelBackground={ panelBackground }
        />

        <ArticlesTilesWithFallback userAccessChecks={ userAccessChecks } />
        <RoadshowsTileWithFallback userAccessChecks={ userAccessChecks } />
        <DealTileWithFallback userAccessChecks={ userAccessChecks }>
          <IssueTile profileType={ profileType } defaultExpandedRowIndexes={ [0] } />
        </DealTileWithFallback>
        { isCorporatePage && !showTopCompanies ? (
          <AbsMarketChart className={ styles.chartWrapperFullWidth } />
        ) : (
          <Flex direction={ { sm: 'column', md: 'column', xl: 'row' } }>
            <TopCompanies className={ styles.topParentsWrapper } />
            <AbsMarketChart className={ styles.chartWrapper } />
          </Flex>
        ) }
        <BondsTileWithFallback userAccessChecks={ userAccessChecks } />
        <TraceTileWithFallback userAccessChecks={ userAccessChecks } />
        <ResearchTile />
        { profileType === ProfileTypes.OVERVIEW && (
          <StatsTileWithFallback userAccessChecks={ userAccessChecks } />
        ) }
      </div>
      <MissingSomethingModalButton />
    </>
  );
};

export default Profile;
